import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';

import { IncomingInventorizationOrder, InventorizationStatusData } from '../services/configApi';
import { NOTE_MAX_LENGTH, getIncorrectStatusLabel, getStatusLabel } from '../utils/inventorizationHelpers';
import {
  getFullyNamedOrderOption,
  getIncomingOrderLabel,
  getRecordOrderOption,
} from '../utils/inventorizationOrder';
import {
  formatQuantity,
  isQuantityProvided,
  isQuantityValid,
  parseQuantity,
} from '../utils/inventorizationQuantity';

interface InventorizationRecordDialogProps {
  /** The record the operator opened, or null while the dialog fades out. */
  record: InventorizationStatusData | null;
  placeAddress?: string;
  disabled?: boolean;
  /** Shown here because the panel behind the dialog is out of sight. */
  error?: string | null;
  /** The orders the record can be attributed to. */
  incomingOrders: IncomingInventorizationOrder[];
  findIncomingOrderById: (orderId: number) => IncomingInventorizationOrder | undefined;
  onSave: (
    record: InventorizationStatusData,
    quantity: number | null,
    note: string | null,
    orderId: number | null,
  ) => void;
  onClose: () => void;
}

/**
 * Everything recorded for one scanned pallet. The box count, the note and the incoming order
 * are editable: the rest is what the scan found and correcting it would mean scanning again.
 */
export const InventorizationRecordDialog: React.FC<InventorizationRecordDialogProps> = ({
  record,
  placeAddress,
  disabled,
  error,
  incomingOrders,
  findIncomingOrderById,
  onSave,
  onClose,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [order, setOrder] = useState<IncomingInventorizationOrder | null>(null);

  useEffect(() => {
    setQuantity(formatQuantity(record?.quantity));
    setNote(record?.note ?? '');
    setOrder(record ? getRecordOrderOption(record) : null);
  }, [record]);

  const selectedOrder = getFullyNamedOrderOption(order, findIncomingOrderById);

  const orderOptions = useMemo(() => (
    selectedOrder && !incomingOrders.some((option) => option.order_id === selectedOrder.order_id)
      ? [selectedOrder, ...incomingOrders]
      : incomingOrders
  ), [incomingOrders, selectedOrder]);

  const isMalformed = !isQuantityValid(quantity);
  const canSave = isQuantityProvided(quantity);

  const handleSave = () => {
    if (!record || !canSave) return;
    onSave(record, parseQuantity(quantity), note.trim() || null, order?.order_id ?? null);
  };

  return (
    <Dialog open={!!record} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {translate('osfc.storage_ui.inventorization.record_dialog.title')}
      </DialogTitle>
      {record && (
        <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {error && (
            <Typography variant="body2" style={{ color: theme.palette.error.main, fontWeight: 'bold' }}>
              {error}
            </Typography>
          )}
          <TextField
            label={translate('osfc.inventorization.columns.place_address')}
            variant="outlined"
            size="small"
            fullWidth
            value={placeAddress ?? '-'}
            disabled
          />
          <TextField
            label={translate('osfc.inventorization.columns.sscc')}
            variant="outlined"
            size="small"
            fullWidth
            value={record.sscc ?? '-'}
            disabled
          />
          <TextField
            label={translate('osfc.inventorization.columns.status')}
            variant="outlined"
            size="small"
            fullWidth
            value={getStatusLabel(record.status, translate)}
            disabled
          />
          <TextField
            label={translate('osfc.inventorization.columns.incorrect_status')}
            variant="outlined"
            size="small"
            fullWidth
            value={getIncorrectStatusLabel(record.incorrect_status, translate)}
            disabled
          />
          <TextField
            label={translate('osfc.inventorization.columns.pallet_id')}
            variant="outlined"
            size="small"
            fullWidth
            value={record.pallet_id ?? '-'}
            disabled
          />
          <TextField
            label={translate('osfc.inventorization.columns.pallet_ext_id')}
            variant="outlined"
            size="small"
            fullWidth
            value={record.pallet_ext_id ?? '-'}
            disabled
          />
          <Autocomplete<IncomingInventorizationOrder, false, false, false>
            options={orderOptions}
            getOptionLabel={getIncomingOrderLabel}
            getOptionSelected={(option, value) => option.order_id === value.order_id}
            value={selectedOrder}
            onChange={(_event, picked) => setOrder(picked)}
            disabled={disabled}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('osfc.inventorization.columns.order')}
                variant="outlined"
              />
            )}
          />
          <TextField
            label={translate('osfc.inventorization.columns.note')}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            value={note}
            onChange={(e) => setNote(e.target.value)}
            helperText={translate('osfc.storage_ui.inventorization.note_characters', { count: note.length, max: NOTE_MAX_LENGTH })}
            inputProps={{ maxLength: NOTE_MAX_LENGTH }}
          />
          <TextField
            label={translate('osfc.inventorization.columns.quantity')}
            variant="outlined"
            size="small"
            fullWidth
            required
            autoFocus
            value={quantity}
            onChange={(e) => setQuantity(e.target.value.trim())}
            error={isMalformed}
            helperText={isMalformed
              ? translate('osfc.storage_ui.inventorization.invalid_quantity')
              : undefined}
          />
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose} disabled={disabled}>
          {translate('osfc.buttons.cancel')}
        </Button>
        <Button color="primary" variant="contained" onClick={handleSave} disabled={disabled || !canSave}>
          {translate('osfc.storage_ui.inventorization.record_dialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
